import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AvatarSessionSummary,
  createAvatarSession,
  FormatInfo,
  getVideoDetail,
  listAvatarSessions,
  transcribeVideoAudio,
  VideoDetail,
} from "../api";
import { formatBytes } from "../utils";
import PersonaPicker from "../components/avatar/PersonaPicker";
import BackgroundPicker from "../components/avatar/BackgroundPicker";
import {
  BG_CATALOG,
  PERSONA_CATALOG,
} from "../components/avatar/catalog";

type FormatId = "webm" | "mp4" | "mov";

const FORMATS: { id: FormatId; label: string; note: string }[] = [
  { id: "webm", label: "WebM", note: "Native — available immediately" },
  { id: "mp4", label: "MP4", note: "Universal (iMovie, Keynote, social)" },
  { id: "mov", label: "MOV", note: "Apple-friendly (QuickTime, Final Cut)" },
];

const LANG_OPTIONS = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "de", label: "Deutsch" },
  { code: "he", label: "עברית" },
];

export default function AdminVideo() {
  const { videoId } = useParams<{ videoId: string }>();
  const nav = useNavigate();
  const [detail, setDetail] = useState<VideoDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<FormatId | null>(null);
  const pollRef = useRef<number | null>(null);

  // Avatar generation state — same logic as AvatarBuilderPanel but with
  // reuse_video_id = current video. Backend batch wiring will land at Bloc 1;
  // for now single combo (1×1) actually creates a session, multi shows a
  // preview-only message.
  const [personIds, setPersonIds] = useState<string[]>(["01_henri"]);
  const [bgIds, setBgIds] = useState<string[]>(["fond1_bureau_vitre"]);
  const [langs, setLangs] = useState<string[]>(["fr"]);
  // Le texte parlé par l'avatar = transcription Whisper de la bande-son
  // de la vidéo source, auto-générée côté backend (OpenAI whisper-1) puis
  // cachée sur le record vidéo (transcriptText). Modifiable avant la
  // génération si l'admin veut retoucher.
  const [transcript, setTranscript] = useState("");
  const [transcribing, setTranscribing] = useState(false);
  const [transcriptErr, setTranscriptErr] = useState<string | null>(null);
  const [transcriptCached, setTranscriptCached] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarErr, setAvatarErr] = useState<string | null>(null);
  const [sessions, setSessions] = useState<AvatarSessionSummary[]>([]);
  const transcribeRequestedRef = useRef(false);
  const sessionPollRef = useRef<number | null>(null);

  const jobCount = personIds.length * bgIds.length * langs.length;
  const isBatch = personIds.length > 1 || bgIds.length > 1;

  const toggleLang = (code: string) => {
    setLangs((prev) => {
      const has = prev.includes(code);
      if (has) {
        const next = prev.filter((c) => c !== code);
        return next.length ? next : prev;
      }
      if (prev.length >= 5) return prev;
      return [...prev, code];
    });
  };

  const submitAvatar = async () => {
    if (!videoId) return;
    setAvatarErr(null);
    if (isBatch) {
      setAvatarErr(
        "Multi-batch (preview UI uniquement) — le backend batch n'est pas encore branché. Choisis 1 personnage et 1 fond pour générer maintenant."
      );
      return;
    }
    setAvatarBusy(true);
    try {
      const r = await createAvatarSession({
        person_id: personIds[0],
        bg_id: bgIds[0],
        voice_id: "vox-04",
        langs,
        script_text: transcript.trim(),
        reuse_video_id: videoId,
      });
      nav(r.share_path);
    } catch (e: unknown) {
      setAvatarErr(e instanceof Error ? e.message : "Échec");
    } finally {
      setAvatarBusy(false);
    }
  };

  // Load detail and (re-)poll until all formats are ready
  useEffect(() => {
    if (!videoId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const d = await getVideoDetail(videoId);
        if (cancelled) return;
        setDetail(d);

        const allReady = d.webm.ready && d.mp4.ready && d.mov.ready;
        if (!allReady) {
          // Schedule next poll in 5s
          pollRef.current = window.setTimeout(load, 5000);
        }
      } catch (e: any) {
        if (!cancelled) setErr(e.message || "Failed to load video");
      }
    };
    load();

    return () => {
      cancelled = true;
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, [videoId]);

  // List avatar sessions tied to this video — auto-dispatched ones don't
  // have their ID on the link record, so we query by reuse_video_id. Polls
  // every 5s while any session is still running so the live status updates.
  useEffect(() => {
    if (!videoId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const list = await listAvatarSessions({ videoId });
        if (cancelled) return;
        setSessions(list);
        const anyRunning = list.some(
          (s) => s.status === "queued" || s.status === "processing"
        );
        if (anyRunning) {
          sessionPollRef.current = window.setTimeout(load, 5000);
        }
      } catch {
        // Silent — not fatal, the rest of the page still works
      }
    };
    load();
    return () => {
      cancelled = true;
      if (sessionPollRef.current) window.clearTimeout(sessionPollRef.current);
    };
  }, [videoId]);

  // Whisper transcription — kicked off once the MP4 is ready. Backend
  // caches the result on the video record so subsequent opens return
  // instantly (cached=true).
  useEffect(() => {
    if (!videoId) return;
    if (!detail || !detail.mp4.ready) return;
    if (transcribeRequestedRef.current) return;
    transcribeRequestedRef.current = true;

    let cancelled = false;
    setTranscribing(true);
    setTranscriptErr(null);
    transcribeVideoAudio(videoId)
      .then((r) => {
        if (cancelled) return;
        setTranscript(r.transcript);
        setTranscriptCached(r.cached);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setTranscriptErr(
          e instanceof Error ? e.message : "Échec de la transcription"
        );
      })
      .finally(() => {
        if (!cancelled) setTranscribing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [videoId, detail]);

  const downloadFormat = async (id: FormatId, info: FormatInfo) => {
    if (!info.ready || !info.download_url) return;
    setErr(null);
    setDownloading(id);
    try {
      // Fetch via blob so we bypass browser "open in new tab" on presigned URLs
      const res = await fetch(info.download_url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();

      const ext = id;
      const filename = `${detail?.filename_base ?? "video"}.${ext}`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e: any) {
      setErr(`${id.toUpperCase()} download failed: ${e.message}`);
    } finally {
      setDownloading(null);
    }
  };

  const downloadOne = async (url: string, filename: string) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const [downloadingAll, setDownloadingAll] = useState(false);
  const downloadAll = async () => {
    if (!detail) return;
    setDownloadingAll(true);
    setErr(null);
    const base = detail.filename_base || "video";
    const jobs: Array<Promise<void>> = [];
    // Raw formats — only the ones that have a presigned URL ready.
    for (const f of FORMATS) {
      const info = detail[f.id];
      if (info?.ready && info.download_url) {
        jobs.push(downloadOne(info.download_url, `${base}.${f.id}`));
      }
    }
    // Every avatar mp4 (per persona × bg × lang).
    for (const s of sessions) {
      if (!s.final_download_urls) continue;
      for (const [lang, url] of Object.entries(s.final_download_urls)) {
        jobs.push(
          downloadOne(url, `${base}__avatar-${s.person_id}-${s.bg_id}-${lang}.mp4`)
        );
      }
    }
    try {
      await Promise.all(jobs);
    } catch (e: any) {
      setErr(`Téléchargement groupé : ${e.message ?? e}`);
    } finally {
      setDownloadingAll(false);
    }
  };

  const hasAvatarDownloads = sessions.some(
    (s) => s.final_download_urls && Object.keys(s.final_download_urls).length > 0
  );

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-4">
      <Link
        to="/admin"
        className="text-sm text-concrete hover:text-charcoal inline-flex items-center gap-1"
      >
        ← Back to videos
      </Link>

      {err && (
        <div className="text-red-600 bg-red-50 p-3 rounded-lg text-sm">{err}</div>
      )}

      {detail ? (
        <>
          {/* Avatar sessions — top of the page, with inline previews + per-lang
              download buttons + "Télécharger tout" (raw formats + every avatar).
              Auto-polls every 5s while any session is running. */}
          {sessions.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 space-y-4">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <div>
                  <p className="font-mono text-[10.5px] uppercase tracking-wide text-concrete">
                    Avatars · auto-générés à partir de cette vidéo
                  </p>
                  <h2 className="font-sans text-xl font-semibold tracking-tight-grot mt-1">
                    {sessions.length} session{sessions.length > 1 ? "s" : ""} avatar
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={downloadAll}
                  disabled={downloadingAll}
                  className="px-3 py-2 rounded-lg bg-charcoal text-ivory text-xs font-mono uppercase tracking-wider disabled:opacity-50"
                >
                  {downloadingAll ? "Téléchargement…" : "Télécharger tout"}
                </button>
              </div>

              <ul className="space-y-4">
                {sessions.map((s) => {
                  const persona = PERSONA_CATALOG.find((p) => p.id === s.person_id);
                  const bg = BG_CATALOG.find((b) => b.id === s.bg_id);
                  const isRunning = s.status === "queued" || s.status === "processing";
                  const isFailed = s.status === "failed";
                  const isDone = s.status === "done";
                  const statusColor = isDone
                    ? "text-green-700 bg-green-50 border-green-200"
                    : isFailed
                    ? "text-red-700 bg-red-50 border-red-200"
                    : "text-amber-800 bg-amber-50 border-amber-200";
                  const urls = s.final_download_urls || {};
                  const langKeys = Object.keys(urls);
                  return (
                    <li
                      key={s.session_id}
                      className="rounded-lg border border-stone bg-paper p-3 space-y-3"
                    >
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div className="text-sm font-medium text-charcoal">
                          {persona?.name ?? s.person_id}
                          <span className="text-concrete"> · </span>
                          {bg?.name ?? s.bg_id}
                          <span className="ml-2 text-xs text-concrete font-mono">
                            {s.langs.join(" / ")}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-block rounded border px-2 py-0.5 text-[10.5px] font-mono uppercase ${statusColor}`}
                          >
                            {isRunning ? s.pipeline_step || s.status : s.status}
                          </span>
                          <Link
                            to={s.share_path}
                            className="text-[11px] font-mono uppercase text-charcoal hover:underline"
                          >
                            timeline →
                          </Link>
                        </div>
                      </div>

                      {s.created_by?.startsWith("auto:") && (
                        <div className="text-[10.5px] text-concrete font-mono">
                          Auto-dispatched · {s.created_at.slice(0, 19).replace("T", " ")}
                        </div>
                      )}

                      {isDone && langKeys.length > 0 && (
                        <div
                          className={`grid gap-3 ${
                            langKeys.length === 1
                              ? "grid-cols-1"
                              : langKeys.length === 2
                              ? "grid-cols-1 sm:grid-cols-2"
                              : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
                          }`}
                        >
                          {langKeys.map((lang) => (
                            <div key={lang} className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <span className="font-mono text-[10.5px] uppercase text-charcoal">
                                  {lang}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    downloadOne(
                                      urls[lang],
                                      `${detail.filename_base || "video"}__avatar-${s.person_id}-${s.bg_id}-${lang}.mp4`
                                    ).catch((e) =>
                                      setErr(`Download ${lang}: ${e.message ?? e}`)
                                    )
                                  }
                                  className="text-[11px] font-mono uppercase text-charcoal hover:underline"
                                >
                                  ⬇ télécharger
                                </button>
                              </div>
                              <video
                                src={urls[lang]}
                                controls
                                playsInline
                                preload="metadata"
                                className="w-full rounded-md bg-black aspect-[9/16]"
                              />
                            </div>
                          ))}
                        </div>
                      )}

                      {isRunning && (
                        <p className="text-[11px] text-concrete font-mono">
                          Pipeline en cours · cette section se rafraîchit toutes les 5 s.
                        </p>
                      )}
                      {isFailed && s.pipeline_error && (
                        <p className="text-[11px] text-red-700 font-mono">
                          {s.pipeline_error}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>

              {!hasAvatarDownloads && (
                <p className="text-[10.5px] text-concrete font-mono">
                  Les vidéos avatar finales apparaîtront ici dès que chaque
                  session passera en « done » (≈ 1 min par langue après record).
                </p>
              )}
            </div>
          )}

          <video
            src={detail.preview_url}
            controls
            playsInline
            className="max-w-full max-h-[80vh] mx-auto rounded-xl bg-black"
          />

          {detail.avatar_session_id && (
            <div className="rounded-xl border border-lemonade/60 bg-ivory-tint p-4 space-y-2">
              <div className="text-sm font-semibold text-charcoal">Session avatar</div>
              <p className="text-sm text-concrete">
                Cette vidéo provient d’un lien créateur d’avatar. Quand le statut est « ready »,
                tu peux ouvrir la session et lancer le pipeline.
              </p>
              {detail.status === "ready" ? (
                <Link
                  to={`/avatar/sessions/${detail.avatar_session_id}/share`}
                  className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-charcoal text-ivory text-sm font-semibold hover:opacity-95 transition"
                >
                  Lancer la génération avatar
                </Link>
              ) : (
                <p className="text-xs font-mono text-concrete">
                  Statut enregistrement : {detail.status} — reviens quand c’est « ready » (traitement
                  en cours).
                </p>
              )}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Download as</div>
              {(!detail.mp4.ready || !detail.mov.ready) && (
                <div className="flex items-center gap-1.5 text-xs text-concrete">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lemonade/80 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-lemonade" />
                  </span>
                  Converting in background…
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {FORMATS.map((f) => {
                const info = detail[f.id];
                const disabled = !info.ready || downloading !== null;
                const busy = downloading === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => downloadFormat(f.id, info)}
                    disabled={disabled}
                    className={`px-4 py-3 rounded-lg text-left transition border ${
                      info.ready
                        ? busy
                          ? "bg-charcoal text-ivory border-charcoal"
                          : "bg-stone/40 border-stone hover:bg-stone/70 text-charcoal"
                        : "bg-stone/10 border-stone/30 text-concrete/50 cursor-not-allowed"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold">{f.label}</span>
                      <span className="text-xs opacity-70">
                        {busy ? "…" : info.ready ? "⬇" : "⚙"}
                      </span>
                    </div>
                    <div className="text-xs mt-0.5 opacity-80">
                      {info.ready
                        ? info.size_bytes
                          ? formatBytes(info.size_bytes)
                          : f.note
                        : "Processing…"}
                    </div>
                  </button>
                );
              })}
            </div>

            {detail.convert_error && (
              <div className="text-xs text-red-600 bg-red-50 p-2 rounded-lg mt-2">
                <b>Transcode error:</b> {detail.convert_error}
              </div>
            )}

            <div className="text-[11px] text-concrete/70 pt-2 border-t border-stone">
              WebM is the raw recording — available instantly. MP4 and MOV are
              transcoded server-side (H.264 + AAC) and usually ready within ~30s
              of upload. This page auto-refreshes every 5s.
            </div>
          </div>


          {/* Génération d'avatars à partir de cette vidéo */}
          <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 space-y-5">
            <div>
              <p className="font-mono text-[10.5px] uppercase tracking-wide text-concrete">
                Avatar · à partir de cette vidéo
              </p>
              <h2 className="font-sans text-2xl sm:text-3xl font-semibold tracking-tight-grot leading-[1.05] mt-1">
                Générer une version avatar
              </h2>
              <p className="text-sm text-concrete mt-2">
                Cette vidéo sert de référence de motion + script. Le pipeline HeyGen
                produit une vidéo verticale (9:16) par combinaison persona × fond × langue.
              </p>
            </div>

            {!detail.mp4.ready && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Le transcodage MP4 n'est pas encore terminé. Le pipeline préfère le
                MP4 comme source — la génération sera plus fiable une fois le transcode
                fini.
              </p>
            )}

            <PersonaPicker
              selected={personIds}
              onChange={(next) =>
                setPersonIds(next.length ? next : [PERSONA_CATALOG[0].id])
              }
              bgIds={bgIds}
              sectionNumber="01"
              allowEmpty={false}
            />

            <BackgroundPicker
              selected={bgIds}
              onChange={(next) =>
                setBgIds(next.length ? next : [BG_CATALOG[0].id])
              }
              personIds={personIds}
              sectionNumber="02"
              allowEmpty={false}
            />

            <section className="flex flex-col gap-3">
              <h2 className="font-mono text-[10.5px] uppercase text-concrete flex items-center gap-2">
                <span>03</span>
                <span className="font-sans text-[15px] font-semibold text-charcoal normal-case">
                  Langues
                </span>
                <span className="ml-auto font-mono text-[10.5px] text-concrete normal-case tabular-nums">
                  {langs.length}/5 sélectionnée{langs.length > 1 ? "s" : ""}
                </span>
              </h2>
              <div className="flex flex-wrap gap-1.5">
                {LANG_OPTIONS.map((l) => {
                  const on = langs.includes(l.code);
                  return (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => toggleLang(l.code)}
                      className={`h-8 px-3.5 text-[11px] font-mono uppercase rounded border ${
                        on
                          ? "bg-charcoal text-ivory border-charcoal"
                          : "border-charcoal bg-transparent text-charcoal opacity-70 hover:opacity-100"
                      }`}
                    >
                      {l.code}
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h2 className="font-mono text-[10.5px] uppercase text-concrete flex items-center gap-2">
                  <span>04</span>
                  <span className="font-sans text-[15px] font-semibold text-charcoal normal-case">
                    Transcription bande-son
                  </span>
                </h2>
                {!transcribing && transcript && (
                  <span className="font-mono text-[10.5px] text-concrete">
                    {transcriptCached ? "depuis le cache" : "Whisper · auto"}
                  </span>
                )}
              </div>
              <p className="text-xs text-concrete -mt-1">
                Auto-transcrit depuis l'audio de cette vidéo (OpenAI Whisper) —
                c'est ce que l'avatar va prononcer. Modifiable ci-dessous si tu
                veux retoucher avant la génération.
              </p>
              {transcribing && (
                <div className="flex items-center gap-2 text-sm text-concrete bg-ivory-tint border border-stone rounded-lg px-3 py-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lemonade/80 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-lemonade" />
                  </span>
                  Transcription en cours… (peut prendre 20-60s selon la durée
                  de la vidéo)
                </div>
              )}
              {transcriptErr && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  <b>Échec transcription :</b> {transcriptErr}
                  <br />
                  <span className="text-xs">
                    Tu peux saisir le texte à la main ci-dessous pour continuer.
                  </span>
                </div>
              )}
              <textarea
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                disabled={transcribing}
                rows={4}
                className="w-full rounded-lg border border-stone bg-paper px-3 py-2 text-sm font-sans text-charcoal disabled:bg-stone/20 disabled:text-concrete"
                placeholder={
                  transcribing
                    ? "Transcription Whisper en cours…"
                    : "Le texte transcrit apparaîtra ici. Tu peux aussi le saisir à la main."
                }
              />
            </section>

            {/* Récap matrice */}
            <section className="rounded-lg border border-charcoal bg-ivory-tint p-3 flex items-center justify-between gap-3">
              <div className="text-xs text-charcoal font-mono leading-relaxed">
                <span className="uppercase tracking-wide text-concrete">
                  À générer ·{" "}
                </span>
                {personIds.length} personnage{personIds.length > 1 ? "s" : ""}
                {" × "}
                {bgIds.length} fond{bgIds.length > 1 ? "s" : ""}
                {" × "}
                {langs.length} langue{langs.length > 1 ? "s" : ""}
              </div>
              <div className="font-mono text-2xl font-bold text-charcoal tabular-nums">
                {jobCount}
              </div>
            </section>

            {avatarErr && <p className="text-sm text-red-700">{avatarErr}</p>}

            <button
              type="button"
              disabled={
                avatarBusy ||
                transcribing ||
                !transcript.trim() ||
                langs.length === 0 ||
                personIds.length === 0 ||
                bgIds.length === 0
              }
              onClick={submitAvatar}
              className="h-[52px] w-full bg-charcoal text-ivory font-mono text-xs uppercase tracking-wider rounded-lg disabled:opacity-50"
            >
              {avatarBusy
                ? "Génère…"
                : isBatch
                ? `Lancer batch — ${jobCount} vidéos (preview)`
                : "Lancer la génération avatar"}
            </button>

            {isBatch && (
              <p className="text-[10.5px] text-concrete font-mono -mt-3">
                Preview UI · le backend batch sera branché au Bloc 1. Pour générer
                maintenant, réduis à 1 personnage et 1 fond.
              </p>
            )}
          </div>
        </>
      ) : (
        !err && <div className="text-concrete">Loading…</div>
      )}
    </div>
  );
}
